import {AfterViewInit, Directive, Input, TemplateRef, ViewContainerRef} from '@angular/core';
import {UntypedFormArray, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {FileDto} from '../../models/file';
import {
  AttachmentFile,
  ConditionalGroup,
  GroupOp, ConditionalItem,
  PosibleAnswer,
  Question,
  QuestionAttachment
} from '../../models/form';
import {TableInputConfig} from '../../shared/components/inputs/table-input/table-input.component';
import {distinctUntilChanged, map, startWith, tap} from 'rxjs/operators';
import {combineLatest, Observable} from 'rxjs';
import {AppFormService} from './app-form.service';

export enum QUESTION_TYPES {
  TEXT = 1,
  LIST,
  SINGLE_OPTION,
  MULTIPLE_ANSWERS,
  ANSWERS_LIST,
  FILES,
  STATUS,
  TABLE,
  TEMPLATE,
  DATE_RANGE,
  DATE_PICKER,
}

interface BaseQuestionParams {
  attachments: AttachmentFile[];
  commentAllowed: boolean;
  commentRequired: boolean;
  attachAllowed: boolean;
  attachRequired: boolean;
  description: string;
  explanation: string;
  isRequired: boolean;
  conditionalGroup?: ConditionalGroup;
  tag?: string
}

export interface QuestionTextParams extends BaseQuestionParams {
  type: QUESTION_TYPES.TEXT;
}

export interface QuestionListParams extends BaseQuestionParams {
  type: QUESTION_TYPES.LIST;
  items: PosibleAnswer[];
}

export interface QuestionSingleParams extends BaseQuestionParams {
  type: QUESTION_TYPES.SINGLE_OPTION;
  items: PosibleAnswer[];
}

export interface QuestionMultipleParams extends BaseQuestionParams {
  type: QUESTION_TYPES.MULTIPLE_ANSWERS;
  items: PosibleAnswer[];
}

export interface QuestionAnswersListParams extends BaseQuestionParams {
  type: QUESTION_TYPES.ANSWERS_LIST;
}

export interface QuestionFilesParams extends BaseQuestionParams {
  type: QUESTION_TYPES.FILES;
  files: FileDto[];
}

export interface QuestionStatusParams extends BaseQuestionParams {
  type: QUESTION_TYPES.STATUS;
  colorItems: PosibleAnswer[];
}

export interface QuestionTableParams extends BaseQuestionParams {
  type: QUESTION_TYPES.TABLE;
  tableConfig: TableInputConfig;
}

export interface QuestionTemplateParams extends BaseQuestionParams {
  type: QUESTION_TYPES.TEMPLATE;
  template: number;
}

export type QuestionParameters =
  QuestionTextParams |
  QuestionListParams |
  QuestionSingleParams |
  QuestionMultipleParams |
  QuestionAnswersListParams |
  QuestionFilesParams |
  QuestionStatusParams |
  QuestionTableParams |
  QuestionTemplateParams;

export interface QuestionData<T> {
  questionId: number;
  answerId?: number;
  answer: T;
  comments: string[];
  attachments: QuestionAttachment[];
}

@Directive({
  selector: '[appQuestion]',
  exportAs: 'AppQuestion'
})
export class FormQuestion implements AfterViewInit {

  @Input() appQuestion: Question;

  private parentForm: UntypedFormGroup;
  private answer: UntypedFormControl;
  private questionControl: UntypedFormGroup;

  private _param: QuestionParameters;
  @Input()
  set appQuestionParams(value: QuestionParameters) {
    this._param = value;
  }
  get param(): QuestionParameters {
    return this._param;
  }

  @Input()
  set appQuestionParentForm(group: UntypedFormGroup) {
    this.parentForm = group;
    this.questionControl = group.controls[this.appQuestion?.id] as UntypedFormGroup;
    this.comments = this.questionControl.get('comments') as UntypedFormArray;
    this.attachments = this.questionControl.get('attachments') as UntypedFormControl;
    this.answer = this.questionControl.get('answer') as UntypedFormControl;
  }

  @Input() questions: Question[];

  public comments: UntypedFormArray;
  private attachments: UntypedFormControl;

  constructor(
    // tslint:disable-next-line:no-any
    private templateRef: TemplateRef<any>,
    private appFormService: AppFormService,
    private viewContainer: ViewContainerRef) {
  }

  setAttachments(files: FileDto[]) {
    this.attachments.setValue(files.map(f => f?.id));
    this._param.attachments = files;
  }

  ngAfterViewInit() {
    if (this.param && this.param.conditionalGroup) {
      this.getComparatorObsForGroup(this.param.conditionalGroup)
        .subscribe(({log, value: show}) => {
          log.forEach(l => console.log(l));
          console.log(`Resultado: ${show}`);
          if (show) {
            this.questionControl.enable();
            if (!this.viewContainer.length) {
              this.viewContainer.createEmbeddedView(this.templateRef);
            }
          } else {
            this.questionControl.disable();
            this.viewContainer.clear();
          }
        });
    } else {
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
  }

  // static ngTemplateContextGuard(dir: FormQuestion, ctx: unknown): ctx is ExampleContext { return true; };

  private getComparatorObsForItem(conditionalItem: ConditionalItem, index) {
    const questionToCompare = this.questions.find(q => q?.id === conditionalItem.questionId);

    if (questionToCompare?.id === this.appQuestion?.id) {
      throw new Error('Conditional item dependent of self\'s value.');
    }

    const answerControl = this.parentForm.controls[questionToCompare?.id] && this.parentForm.controls[questionToCompare?.id].get('answer');
    const inputComponent = this.appFormService.getComponentControl(questionToCompare?.questionTypeId);

    return answerControl?.valueChanges
      .pipe(
        startWith(answerControl?.value),
        distinctUntilChanged(),
        map(value => {
          const questionName = questionToCompare?.description.slice(0, 20) ?? '';
          console.log(`Disparado: *${questionName}* con valor {${value}} `);
          const result = inputComponent.compare(value, conditionalItem.comparableOperator, conditionalItem.comparableValue);
          return {
            value: result,
            // tslint:disable-next-line:max-line-length
            log: `${new Array(index).join('|...')} ITEM: *${questionName}* (${inputComponent.constructor.name}) ${conditionalItem.comparableOperator} to ${conditionalItem.comparableValue}: [${result}]`,
          };
        }),
        tap(result => {
        })
      );
  }

  private getComparatorObsForGroup(conditionalGroup: ConditionalGroup, index = 1): Observable<{log: string[], value: boolean}> {
    const op = conditionalGroup.operator;
    // console.log(`${new Array(index).join('|...')} GRUPO con operador: ${op}.`);

    const items = conditionalGroup?.items?.map(i => this.getComparatorObsForItem(i, index + 1));
    const groups = conditionalGroup?.childGroups?.map(g => this.getComparatorObsForGroup(g, index + 1));


    return combineLatest([
      ...items,
      ...groups,
    ]).pipe(
      map(values => {
        // Obtener los logs de grupos e items
        const logs = values.map(v => v.log).reduce<string[]>((p, c) => {
          return ((typeof c === 'string') ? [c] : c).concat(p);
        }, []);

        // Calcular el resultado del grupo
        let result: boolean;
        switch (op) {
          case GroupOp.AND:
            result = values.map(v => v.value).every(v => !!v);
            break;
          case GroupOp.OR:
            result = values.map(v => v.value).some(v => !!v);
            break;
        }

        // Agregar el log de grupo
        logs.unshift(`${new Array(index).join('|...')}GRUPO con operador: ${op}: [${result}]`);

        return {
          log: logs,
          value: result,
        };
      }),
    );
  }

}

